import axios, { AxiosError, AxiosHeaders, type InternalAxiosRequestConfig } from 'axios'
import { API_ORIGIN } from '@/config/api'
import { clearAllTokens, getRefreshToken, getToken, setRefreshToken, setToken } from './token'

// Custom params serializer for FastAPI compatibility
// FastAPI expects repeated params for arrays: ?category=A&category=B
const paramsSerializer = (params: Record<string, any>) => {
  const parts: string[] = []
  for (const key in params) {
    const value = params[key]
    if (value === null || value === undefined) {
      continue
    }
    if (Array.isArray(value)) {
      value.forEach((v) => {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`)
      })
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    }
  }
  return parts.join('&')
}

// Global request timeout so a slow/hung backend can't stall the UI forever.
// 30 s is short enough that a "loading" spinner does not read as frozen,
// long enough that normal heavy queries (map layer fetches, dashboards)
// complete. Endpoints that legitimately need longer (uploads, exports) can
// pass their own `timeout` per-request to override.
const DEFAULT_TIMEOUT_MS = 30_000

export const api = axios.create({
  baseURL: `${API_ORIGIN}/api`,
  withCredentials: false,
  paramsSerializer,
  timeout: DEFAULT_TIMEOUT_MS,
  timeoutErrorMessage: 'La petición tardó demasiado. Inténtalo de nuevo.',
})

export const authApi = axios.create({
  baseURL: API_ORIGIN,
  withCredentials: false,
  paramsSerializer,
  timeout: DEFAULT_TIMEOUT_MS,
  timeoutErrorMessage: 'La petición tardó demasiado. Inténtalo de nuevo.',
})

const attachToken = (cfg: InternalAxiosRequestConfig) => {
  const token = getToken()
  if (token) {
    cfg.headers = cfg.headers instanceof AxiosHeaders ? cfg.headers : new AxiosHeaders(cfg.headers)
    cfg.headers.set('Authorization', `Bearer ${token}`)
  }
  return cfg
}

// Track if we're currently refreshing to prevent multiple refresh calls
let isRefreshing = false
let refreshSubscribers: ((token: string) => void)[] = []

function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb)
}

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token))
  refreshSubscribers = []
}

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    return null
  }

  try {
    // Use a fresh axios instance to avoid interceptor loops
    const response = await axios.post(
      `${API_ORIGIN}/auth/refresh`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    )

    const { access_token, refresh_token } = response.data
    setToken(access_token)
    setRefreshToken(refresh_token)
    return access_token
  } catch (error) {
    // Refresh failed - clear tokens and redirect to login
    clearAllTokens()
    return null
  }
}

const handle401 = async (err: AxiosError) => {
  const originalRequest = err.config as InternalAxiosRequestConfig & { _retry?: boolean }

  // If it's a 401 and we haven't tried to refresh yet
  if (err?.response?.status === 401 && !originalRequest._retry) {
    // Don't try to refresh on auth endpoints (login, refresh itself)
    if (originalRequest.url?.includes('/auth/')) {
      clearAllTokens()
      return Promise.reject(err)
    }

    if (isRefreshing) {
      // Wait for the refresh to complete
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((token: string) => {
          originalRequest.headers.set('Authorization', `Bearer ${token}`)
          resolve(axios(originalRequest))
        })
      })
    }

    originalRequest._retry = true
    isRefreshing = true

    try {
      const newToken = await refreshAccessToken()

      if (newToken) {
        isRefreshing = false
        onTokenRefreshed(newToken)
        originalRequest.headers.set('Authorization', `Bearer ${newToken}`)
        return axios(originalRequest)
      } else {
        // Refresh failed - clear tokens, PrivateLayout will redirect via React Router
        isRefreshing = false
        clearAllTokens()
        return Promise.reject(err)
      }
    } catch (refreshError) {
      isRefreshing = false
      clearAllTokens()
      return Promise.reject(refreshError)
    }
  }

  return Promise.reject(err)
}

// Turn network / timeout / abort errors into a rejection that always carries
// a human-readable `response.data.detail`, so `catch` handlers in modals can
// close their toasts and show a message without special-casing every failure
// mode.
const normalizeNetworkError = (err: AxiosError) => {
  const code = err.code
  const alreadyHasDetail = (err.response?.data as any)?.detail

  if (!err.response && !alreadyHasDetail) {
    let detail = 'Error de red. Comprueba tu conexión e inténtalo de nuevo.'
    if (code === 'ECONNABORTED' || err.message?.includes('timeout')) {
      detail = 'La petición tardó demasiado. Inténtalo de nuevo.'
    } else if (code === 'ERR_CANCELED') {
      // Aborted on purpose (component unmounted, newer request superseded).
      // Propagate the original error but tag it so callers can filter it out.
      ;(err as any).__isCancel = true
    } else if (code === 'ERR_NETWORK') {
      detail = 'No se pudo conectar con el servidor.'
    }
    // Inject a fake response so `err?.response?.data?.detail` in callers works.
    ;(err as any).response = { data: { detail }, status: 0 }
  }
  return Promise.reject(err)
}

api.interceptors.request.use(attachToken)
authApi.interceptors.request.use(attachToken)
api.interceptors.response.use((r) => r, (err) => handle401(err).catch(normalizeNetworkError))
authApi.interceptors.response.use((r) => r, (err) => handle401(err).catch(normalizeNetworkError))
